import type { OrbState } from '../ui/agent-orb';
import type { Dictionary } from '../../i18n';

type ToolLabelKey = keyof Dictionary['qagentDocument']['chatMessage']['toolLabels'];

// Every status the chat shows while the agent works (agent tool names and backend job
// stages), with its translated label and the orb animation that matches it
const TOOL_STATUSES: Record<string, { label: ToolLabelKey; orb: OrbState }> = {
  thinking: { label: 'thinking', orb: 'working' },
  web_search: { label: 'web_search', orb: 'searching' },
  search_rag_database: { label: 'search_rag_database', orb: 'searching' },
  knowledge_search: { label: 'knowledge_search', orb: 'searching' },
  suggest_edit: { label: 'suggest_edit', orb: 'composing' },
  suggest_insert: { label: 'suggest_insert', orb: 'composing' },
  suggest_delete: { label: 'suggest_delete', orb: 'composing' },
  read_section: { label: 'read_section', orb: 'searching' },
  get_diff: { label: 'get_diff', orb: 'searching' },
  plan_review: { label: 'plan_review', orb: 'connecting' },
  check_redline: { label: 'check_redline', orb: 'solving' },
  withdraw_suggestion: { label: 'withdraw_suggestion', orb: 'composing' },
  remember: { label: 'remember', orb: 'working' },
  generate_contract_draft: { label: 'generate_contract_draft', orb: 'composing' },
  export_document: { label: 'export_document', orb: 'shaping' },
  plan_parallel_review: { label: 'plan_parallel_review', orb: 'connecting' },
  section_review: { label: 'section_review', orb: 'solving' },
  merge_results: { label: 'merge_results', orb: 'weaving' },
  generating: { label: 'generating', orb: 'composing' },
  // Contract generation stages (from backend job processor)
  'Processing details': { label: 'processingDetails', orb: 'working' },
  'Processing contract information': { label: 'processingInformation', orb: 'working' },
  'Fetching playbook rules': { label: 'fetchingPlaybookRules', orb: 'searching' },
  'Generating blueprint': { label: 'generatingBlueprint', orb: 'shaping' },
  'Blueprint generated': { label: 'blueprintGenerated', orb: 'shaping' },
  'Preparing sections': { label: 'preparingSections', orb: 'shaping' },
  'Ordering sections': { label: 'orderingSections', orb: 'shaping' },
  'Generating section content': { label: 'generatingSections', orb: 'composing' },
  'Regenerating section': { label: 'regeneratingSection', orb: 'composing' },
  'Assembling results': { label: 'assemblingResults', orb: 'weaving' },
  'Detecting duplicates': { label: 'checkingDuplicates', orb: 'solving' },
  'Removing duplicates': { label: 'removingDuplicates', orb: 'solving' },
  'Polishing content': { label: 'polishingContent', orb: 'weaving' },
  'Final quality check': { label: 'finalQualityCheck', orb: 'weaving' },
  'Validating contract': { label: 'validatingContract', orb: 'solving' },
  'Saving contract': { label: 'savingContract', orb: 'working' },
};

export const getToolStatusLabel = (t: Dictionary, status: string, fallback?: string): string => {
  const known = TOOL_STATUSES[status];
  if (known) return t.qagentDocument.chatMessage.toolLabels[known.label];
  // Unmapped job stages are readable sentences ("Generating section: Scope"), so show them as-is;
  // an unmapped tool name ("TavilySearch", "some_tool") gets the generic label instead
  return /\s/.test(status) ? status : fallback || t.qagentDocument.chatMessage.defaultWorking;
};

/** The label next to the orb while a reply is in progress: the running tool, else the skill, else "Thinking" */
export const workingStatusLabel = (t: Dictionary, status?: string | null, skillId?: string | null): string => {
  const chat = t.qagentDocument.chatMessage;
  if (status && status !== 'thinking') return getToolStatusLabel(t, status);
  return skillId === 'review' ? chat.skillLabels.review : chat.toolLabels.thinking;
};

export const getToolStatusOrb = (status?: string | null): OrbState => {
  if (!status) return 'working';
  const known = TOOL_STATUSES[status];
  if (known) return known.orb;
  // Per-section stages carry a suffix ("Generating section: Scope", "Generating batch 2/5")
  if (status.startsWith('Generating ')) return 'composing';
  return 'working';
};
